<template>
  <div class="mall-shop">
    <div class="page" :class="{ 'page--bottom-bar': currentTab !== 'home' }">
      <MallShopHeader @menu-click="onHeaderMenuClick" />
      <div class="mall-shop__search">
        <MallSearchBar :readonly="true" @click="goSearch" />
      </div>
      <div class="mt-8">
        <MallCategoryTabs :tabs="tabs" :value="currentTab" @change="changeTab" />
      </div>

      <!-- 홈 탭 -->
      <MallShopHome
        v-if="currentTab === 'home'"
        :sections="homeSections"
        :recentTabs="recentTabs"
        :recentItems="recentItems"
        @quick-menu-click="onQuickMenuClick"
        @recent-more-click="onRecentMoreClick"
        @more-click="changeTab"
        @product-click="openPurchaseSheet"
      />

      <!-- 카테고리 탭 -->
      <template v-else>
        <MallProductGrid
          :products="tabProducts"
          :selectedProductId="selectedProductId"
          @select="selectedProductId = $event"
        />
        <MallBottomBar
          :buttons="purchaseButtons"
          @click="openPurchaseSheet(selectedProduct)"
        />
      </template>

      <!-- 카테고리 진입 안내 팝업 -->
      <MallNoticeSheet
        v-if="notice"
        :title="notice.title"
        :messageLines="notice.messageLines"
        @confirm="notice = null"
        @close="notice = null"
      />

      <!-- 상품 구매 (장바구니 담기 / 바로 구매하기) -->
      <MallPurchaseLayer
        :product="purchaseProduct"
        :profileId="profileId"
        :productMap="catalog.productMap"
        @close="purchaseProduct = null"
      />

      <MallToast
        v-if="toast"
        :key="toast.key"
        :title="toast.title"
        :message="toast.message"
        @close="toast = null"
      />
    </div>
  </div>
</template>

<script>
import Constant from '@/constant'
import mall from '@/utilities/mall.js'
import MallShopHeader from '@/components/new/molecules/MallShopHeader.vue'
import MallSearchBar from '@/components/new/molecules/MallSearchBar.vue'
import MallCategoryTabs from '@/components/new/molecules/MallCategoryTabs.vue'
import MallBottomBar from '@/components/new/molecules/MallBottomBar.vue'
import MallToast from '@/components/new/molecules/MallToast.vue'
import MallShopHome from '@/components/new/organisms/MallShopHome.vue'
import MallProductGrid from '@/components/new/organisms/MallProductGrid.vue'
import MallNoticeSheet from '@/components/new/organisms/MallNoticeSheet.vue'
import MallPurchaseLayer from '@/components/new/organisms/MallPurchaseLayer.vue'

// 서버 카테고리 id(device_type 기반)별 화면 설정 - 여기 없는 카테고리는 상단 탭에 노출
// showInTab: 상단 탭 노출 여부 / sectionTitle: 홈 섹션 제목(없으면 카테고리명)
// notice: 탭 진입 시 안내 팝업 (once: true면 한 번만 노출)
// TODO: 소모성재료 하위 분류 탭(혈당검사지, 채혈침 등)은 API에 분류 값이 없어 제외
// TODO: 안내 팝업 노출 여부 및 문구 API
var CATEGORY_UI = {
  DEVICE: { showInTab: false },
  EXPENDABLE: {
    notice: {
      once: false,
      title: '소모성 재료는 분할 배송될 수 있어요',
      messageLines: ['여러 제품을 함께 구매하시면', '제품마다 수령일이 달라질 수 있으니 참고해 주세요.'],
    },
  },
  INSULIN_PUMP: {
    sectionTitle: '무선 인슐린펌프',
    notice: {
      once: false,
      title: '구매 전 의사와 충분히 상담해 주세요',
      messageLines: ['첫 구매 시 사용 안내를 위해', '고객센터(02-6954-7682)에서 연락드립니다.'],
    },
  },
}

export default {
  name: 'MallShop',
  components: {
    MallShopHeader,
    MallSearchBar,
    MallCategoryTabs,
    MallBottomBar,
    MallToast,
    MallShopHome,
    MallProductGrid,
    MallNoticeSheet,
    MallPurchaseLayer,
  },
  data() {
    return {
      // 프로필 선택 화면에서 넘어온 프로필 id
      profileId: Number(this.$route.query.profileId) || null,
      currentTab: 'home',
      selectedProductId: null,
      purchaseProduct: null,
      notice: null,
      shownNoticeKeys: [],
      toast: null,

      // 카테고리 / 전체 상품 (mall.fetchCatalog)
      catalog: {
        categories: [],
        products: [],
        productMap: {},
      },

      // 최근 구매 상품 (최근 구매순, 구매 이력이 없으면 '최근 구매 상품' 영역 미노출)
      recentItems: [],
    }
  },
  computed: {
    categories() {
      return this.catalog.categories.map(category => {
        var ui = CATEGORY_UI[category.id] || {}
        return {
          key: category.id,
          label: category.name,
          showInTab: ui.showInTab !== false,
          sectionTitle: ui.sectionTitle,
        }
      })
    },
    // 보험적용가가 있으면 할인율 / 원가와 함께 노출
    products() {
      return this.catalog.products.map(product => mall.toProductView(product, this.catalog.categories))
    },
    tabs() {
      return [{ key: 'home', label: '홈' }].concat(
        this.categories.filter(c => c.showInTab).map(c => ({ key: c.key, label: c.label }))
      )
    },
    // 최근 구매 상품이 있는 카테고리만
    recentTabs() {
      return this.categories
        .filter(c => this.recentItems.some(item => item.category === c.key))
        .map(c => ({ key: c.key, label: c.label }))
    },
    // 상품이 없는 카테고리는 홈 섹션에서 제외
    homeSections() {
      return this.categories
        .map(c => ({
          key: c.key,
          title: c.sectionTitle || c.label,
          products: this.products.filter(p => p.category === c.key).slice(0, 3),
        }))
        .filter(section => section.products.length > 0)
    },
    tabProducts() {
      return this.products.filter(p => p.category === this.currentTab)
    },
    selectedProduct() {
      return this.products.find(p => p.id === this.selectedProductId) || null
    },
    purchaseButtons() {
      return [
        { key: 'purchase', label: '선택한 상품 구매하기', type: 'primary', disabled: this.selectedProductId === null },
      ]
    },
  },
  mounted() {
    ChannelIO('shutdown')
    this.fetchData()
  },
  methods: {
    fetchData() {
      var token = mall.getAccessToken()
      if (!token) {
        mall.goLogin(this.$router)
        return
      }
      mall.fetchCatalog(this.$store, this.profileId, token)
        .then(catalog => {
          this.catalog = catalog
          this.fetchRecentItems(token)
        })
        .catch(err => {
          if (mall.isAuthError(err)) {
            mall.goLogin(this.$router)
            return
          }
          this.toast = { key: Date.now(), title: '상품을 불러오지 못했어요.', message: mall.errorMessage(err) }
        })
    },
    fetchRecentItems(token) {
      this.$store.dispatch(Constant.FETCH_STORE_ORDERS, { profile_id: this.profileId, page: 1, limit: 10, token: token })
        .then(data => {
          this.recentItems = this.toRecentItems((data && data.orders) || [])
        })
        .catch(err => {
          // 최근 구매 상품을 못 받아도 영역만 미노출
          if (mall.isAuthError(err)) {
            mall.goLogin(this.$router)
          }
        })
    },
    // 결제 이력(최신순) → 최근 구매 상품 (환불 건 제외, 같은 상품은 한 번만)
    // TODO: 주문 응답에 상품 id가 없어 상품명이 같은 판매 상품으로 찾음 (임시)
    toRecentItems(orders) {
      var items = []
      orders.filter(order => !order.is_refund).forEach(order => {
        (order.products || []).forEach(line => {
          var product = this.catalog.products.find(p => p.name === line.name)
          if (!product || items.some(item => item.id === product.id)) return
          items.push(Object.assign(mall.toProductView(product, this.catalog.categories), {
            purchaseDate: mall.formatDate(order.purchased_at).slice(5),
          }))
        })
      })
      return items
    },
    changeTab(key) {
      var category = this.categories.find(c => c.key === key)
      if (key !== 'home' && !(category && category.showInTab)) {
        // TODO: 상단 탭이 없는 카테고리(관리기기)의 자세히보기 화면 연결
        return
      }
      this.currentTab = key
      this.selectedProductId = null
      window.scrollTo(0, 0)
      this.showTabNotice(key)
    },
    showTabNotice(key) {
      var notice = CATEGORY_UI[key] && CATEGORY_UI[key].notice
      if (!notice) return
      if (notice.once && this.shownNoticeKeys.indexOf(key) > -1) return
      this.shownNoticeKeys.push(key)
      this.notice = notice
    },
    openPurchaseSheet(product) {
      if (!product) return
      this.purchaseProduct = product
    },
    goCart() {
      this.$router.push({ path: '/mall/cart', query: { profileId: this.profileId } })
    },
    goSearch() {
      this.$router.push({ path: '/mall/search', query: { profileId: this.profileId } })
    },
    onHeaderMenuClick(key) {
      if (key === 'home') {
        // 마이페이지 홈으로 이동
        this.$router.push({ path: '/home' })
        return
      }
      if (key === 'cart') {
        this.goCart()
        return
      }
      if (key === 'history') {
        this.goHistory()
      }
    },
    onQuickMenuClick(key) {
      if (key === 'cart') {
        this.goCart()
        return
      }
      if (key === 'history') {
        this.goHistory()
        return
      }
      if (key === 'booking') {
        this.$router.push({ path: '/mall/booking', query: { profileId: this.profileId } })
        return
      }
      if (key === 'card') {
        this.$router.push({ path: '/mall/card', query: { profileId: this.profileId } })
        return
      }
      if (key === 'address') {
        this.$router.push({ path: '/mall/address', query: { profileId: this.profileId } })
      }
    },
    goHistory() {
      this.$router.push({ path: '/mall/history', query: { profileId: this.profileId } })
    },
    onRecentMoreClick() {
      // TODO: 최근 구매 상품 전체 보기 화면 연결
    },
  },
}
</script>

<style scoped>
  .mall-shop {
    display: flex;
    width: 100%;
    min-height: 100vh;
    justify-content: center;
    align-items: flex-start;
  }
  .page {
    width: 100%;
    min-height: 100vh;
    background: #FFF;
  }
  /* 하단 고정 버튼 영역(90px)에 마지막 상품이 가려지지 않도록 */
  .page--bottom-bar {
    padding-bottom: 90px;
  }
  .mall-shop__search {
    padding: 4px 16px 0 16px;
  }
  .mt-8 { margin-top: 8px; }
  @media (min-width: 600px) {
    .page {
      width: 600px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/page/new/MallShop.vue